import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { fetchContactEnquiries } from "../../../lib/api";

function cellMessage(value, maxLen = 100) {
  const t = String(value ?? "").trim();
  if (!t) return "—";
  return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
}

function sourceLabel(source) {
  const map = {
    join_now: "Join Now",
    contact: "Contact",
    admission: "Admission",
  };
  return map[String(source ?? "").toLowerCase()] || source || "—";
}

export default function AdminContactEnquiries() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const rowActions = useAdminRowActionsMenu();

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const res = await fetchContactEnquiries();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setListError(err.message || "Could not load enquiries.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bt - at;
    });
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        id: "createdAt",
        header: "Date",
        width: "16%",
        tdClassName: "adm-nowrap",
        render: (row) =>
          row.createdAt ? new Date(row.createdAt).toLocaleString() : "—",
      },
      {
        id: "name",
        header: "Name",
        width: "12%",
        tdClassName: "adm-data-cell-name",
        render: (row) => <strong>{row.name || "—"}</strong>,
      },
      {
        id: "phone",
        header: "Phone",
        width: "11%",
        tdClassName: "adm-data-cell-muted",
        render: (row) => row.phone || "—",
      },
      {
        id: "email",
        header: "Email",
        width: "18%",
        tdClassName: "adm-data-cell-muted",
        render: (row) => {
          const e = String(row.email ?? "").trim();
          if (!e) return "—";
          return (
            <a href={`mailto:${e}`} className="adm-link">
              {e}
            </a>
          );
        },
      },
      {
        id: "classGrade",
        header: "Class",
        width: "8%",
        tdClassName: "adm-data-cell-muted",
        render: (row) => row.classGrade || "—",
      },
      {
        id: "source",
        header: "Form",
        width: "10%",
        render: (row) => sourceLabel(row.source),
      },
      {
        id: "message",
        header: "Message",
        width: "25%",
        tdClassName: "adm-msg-cell",
        render: (row) => cellMessage(row.message),
      },
      buildActionsColumn({
        rowActions,
        getRowLabel: (row) => row.name || "enquiry",
      }),
    ],
    [rowActions]
  );

  return (
    <div className="adm-page adm-page--wide">
      <header className="adm-page-head adm-page-head--row">
        <div>
          <h1 className="adm-page-title">Enquiries</h1>
          <p className="adm-page-desc adm-page-desc--wide">
            Messages from the public site — <strong>Join Now</strong> (footer), <strong>Contact us</strong>, and <strong>Admission enquiry</strong> forms.
          </p>
        </div>
        <button type="button" className="adm-btn adm-btn--ghost" onClick={reload} disabled={loading}>
          Refresh
        </button>
      </header>

      {listError ? (
        <p className="adm-field-error" role="alert">
          {listError}
        </p>
      ) : null}

      <AdminRowActionsMenu
        actionMenu={rowActions.actionMenu}
        onClose={rowActions.close}
        ariaLabel="Enquiry actions"
        showActiveToggle={false}
        onView={(row) => {
          setViewRow(row);
          rowActions.close();
        }}
      />

      {viewRow &&
        createPortal(
          <div className="adm-drawer-root">
            <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={() => setViewRow(null)} />
            <aside className="adm-drawer-panel" role="dialog" aria-modal="true">
              <div className="adm-drawer-panel-inner">
                <header className="adm-drawer-header">
                  <h2 className="adm-drawer-title">View enquiry</h2>
                  <button type="button" className="adm-drawer-close" onClick={() => setViewRow(null)} aria-label="Close">
                    <span aria-hidden>×</span>
                  </button>
                </header>
                <div className="adm-drawer-fields">
                  <p>
                    <strong>Date:</strong> {viewRow.createdAt ? new Date(viewRow.createdAt).toLocaleString() : "—"}
                  </p>
                  <p>
                    <strong>Form:</strong> {sourceLabel(viewRow.source)}
                  </p>
                  <p>
                    <strong>Name:</strong> {viewRow.name || "—"}
                  </p>
                  <p>
                    <strong>Phone:</strong> {viewRow.phone || "—"}
                  </p>
                  <p>
                    <strong>Email:</strong> {viewRow.email || "—"}
                  </p>
                  <p>
                    <strong>Class:</strong> {viewRow.classGrade || "—"}
                  </p>
                  <p>
                    <strong>Message:</strong>
                  </p>
                  <p className="adm-view-message">{viewRow.message || "—"}</p>
                </div>
                <footer className="adm-drawer-footer">
                  <button type="button" className="adm-btn adm-btn--primary" onClick={() => setViewRow(null)}>
                    Close
                  </button>
                </footer>
              </div>
            </aside>
          </div>,
          document.body
        )}

      <section className="adm-card adm-card--table">
        <h2 className="adm-card-title">Submissions ({sortedRows.length})</h2>
        {loading ? (
          <p className="adm-empty">Loading…</p>
        ) : (
          <AdminDataTable
            layout="fixed"
            columns={columns}
            rows={sortedRows}
            getRowKey={(r) => r.id}
            emptyState={
              <span className="adm-empty adm-empty--in-table">
                No enquiries yet. Submissions appear here when visitors use Join Now, Contact, or Admission forms.
              </span>
            }
          />
        )}
      </section>
    </div>
  );
}
